import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	Bot,
	Copy,
	Eye,
	EyeOff,
	Info,
	Loader2,
	Lock,
	Pencil,
	Plus,
	Target,
	Trash2,
	Users,
	X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { fetchAPI, toggleSportSelection } from "../../lib/utils";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { AgentChatSheet } from "../ai/AgentChatSheet";
import { AddSportDialog } from "./AddSportDialog";
import { CreatedSportsCard } from "./CreatedSportsCard";
import { modelCountsText, modelOwnerText, modelTitle } from "./modelLabels";
import { PublishModelDialog } from "./PublishModelDialog";

export const SPORT_CONFIG_GUIDE_KEY = "sport_config_guide_dismissed";

const GUIDE_ROWS = [
	["scGuideSports", "scGuideSportsBody"],
	["scGuideNewSport", "scGuideNewSportBody"],
	["scGuideModel", "scGuideModelBody"],
	["scGuidePublish", "scGuidePublishBody"],
	["scGuideTeam", "scGuideTeamBody"],
	["scGuideBodyweight", "scGuideBodyweightBody"],
];

/**
 * «Sport e modelli»: la sezione con cui un coach configura da se' il proprio
 * lavoro sugli sport, senza passare dall'admin.
 *
 *  - gli sport che segue: li sceglie lui, tranne quelli di tiro (D11); se
 *    uno sport non c'è lo aggiunge, e resta nel suo perimetro finché l'admin
 *    non lo rende di tutti (D1, Fase 3);
 *  - per ogni sport, il modello prestativo di sistema (in lettura, o in
 *    scrittura per l'admin di quella disciplina), il suo modello personale
 *    (crea, modifica, pubblica, ritira, elimina: D9, D12) e quelli dei colleghi
 *    di team (in lettura: D10).
 *
 * Le spiegazioni stanno qui perche' e' qui che si decide: la guida si chiude e
 * resta chiusa, come quella dei coach team.
 * Spec: docs/sport-config/DECISIONS.md, passi 2 e 3.
 */
export function SportConfigTab() {
	const { t } = useLanguage();
	const { updateUser, user } = useAuth();
	const navigate = useNavigate();

	const [config, setConfig] = useState(null);
	const [loading, setLoading] = useState(true);
	const [selectedSports, setSelectedSports] = useState([]);
	const [savingSports, setSavingSports] = useState(false);
	const [busyModelId, setBusyModelId] = useState(null);
	const [publishTarget, setPublishTarget] = useState(null);
	// Lo sport per cui il coach vuole farsi creare il modello dall'assistente
	// (§9octodecies della spec AI): la chat scrive solo il suo modello di quello sport.
	const [assistantSport, setAssistantSport] = useState(null);
	const [addOpen, setAddOpen] = useState(false);
	const [guideOpen, setGuideOpen] = useState(() => {
		try {
			return window.localStorage.getItem(SPORT_CONFIG_GUIDE_KEY) !== "1";
		} catch {
			return true;
		}
	});

	const load = useCallback(async () => {
		try {
			const data = await fetchAPI("/sport-config");
			setConfig(data);
			setSelectedSports(
				Array.isArray(data?.disciplines) ? data.disciplines : [],
			);
		} catch (error) {
			toast.error(`${t("error")}: ${error?.message || ""}`);
		} finally {
			setLoading(false);
		}
	}, [t]);

	useEffect(() => {
		load();
	}, [load]);

	const dismissGuide = () => {
		setGuideOpen(false);
		try {
			window.localStorage.setItem(SPORT_CONFIG_GUIDE_KEY, "1");
		} catch {
			/* la guida si richiude comunque per questa sessione */
		}
	};

	const locked = useMemo(
		() => new Set(config?.locked_disciplines || []),
		[config?.locked_disciplines],
	);
	const sportsByValue = useMemo(() => {
		const map = {};
		(config?.sports || []).forEach((sport) => {
			map[sport.value] = sport;
		});
		return map;
	}, [config?.sports]);

	// Le discipline di tiro le assegna l'admin (D11): quelle assegnate si vedono
	// bloccate, le altre di tiro non si propongono affatto. Gli sport solo
	// «legati» (disattivati, o di un collega che non è più nel team) restano
	// finché li segui, per poterli togliere, ma non si ripropongono.
	const choosable = (config?.sports || []).filter(
		(sport) =>
			(!sport.is_shooting || locked.has(sport.value)) &&
			(sport.selectable !== false || selectedSports.includes(sport.value)),
	);
	const hiddenShooting = (config?.sports || []).some(
		(sport) => sport.is_shooting && !locked.has(sport.value),
	);
	const sportsChanged =
		JSON.stringify(selectedSports) !==
		JSON.stringify(config?.disciplines || []);

	const toggleSport = (value) => {
		if (locked.has(value) && !config?.is_admin) return;
		setSelectedSports((current) => toggleSportSelection(current, value));
	};

	// Le discipline cambiano gli ambiti, e con loro il menu: si rilegge la
	// sessione, e se la lettura fallisce non si azzera l'utente.
	const refreshSession = async () => {
		try {
			updateUser(await fetchAPI("/auth/me"));
		} catch {
			/* il menu si aggiorna al prossimo caricamento */
		}
	};

	const saveSports = async () => {
		setSavingSports(true);
		try {
			await fetchAPI("/sport-config/disciplines", {
				method: "PUT",
				body: JSON.stringify({ disciplines: selectedSports }),
			});
			await refreshSession();
			toast.success(t("scSportsSaved"));
			await load();
		} catch (error) {
			toast.error(`${t("error")}: ${error?.message || ""}`);
		} finally {
			setSavingSports(false);
		}
	};

	// Uno sport creato si segue già (lo fa il server): basta rileggere.
	const onSportCreated = async () => {
		await refreshSession();
		await load();
	};

	// «Esiste già: seguilo» dalla finestra di creazione. Parte da quelli
	// salvati, non dalle scelte ancora da salvare.
	const followSport = async (value) => {
		const saved = config?.disciplines || [];
		if (saved.includes(value)) {
			await load();
			return;
		}
		try {
			await fetchAPI("/sport-config/disciplines", {
				method: "PUT",
				body: JSON.stringify({ disciplines: [...saved, value] }),
			});
			await refreshSession();
			toast.success(t("scSportsSaved"));
			await load();
		} catch (error) {
			toast.error(`${t("error")}: ${error?.message || ""}`);
		}
	};

	const sportBadge = (sport) => {
		if (sport?.library_scope !== "personal") return null;
		if (sport.owner?.user_id && sport.owner.user_id === user?.user_id) {
			return t("scSportMineBadge");
		}
		return t("scSportTeamBadge").replace("{name}", sport.owner?.name || "");
	};

	const openModel = (modelId) =>
		navigate(`/sport-config/models/${encodeURIComponent(modelId)}`);

	const createModel = async (sport, sourceModelId) => {
		setBusyModelId(`create:${sport}`);
		try {
			const created = await fetchAPI("/performance-models", {
				method: "POST",
				body: JSON.stringify({
					sport,
					...(sourceModelId ? { source_model_id: sourceModelId } : {}),
				}),
			});
			toast.success(t("scModelCreated"));
			openModel(created.model_id);
		} catch (error) {
			toast.error(`${t("error")}: ${error?.message || ""}`);
		} finally {
			setBusyModelId(null);
		}
	};

	const withdrawModel = async (model) => {
		if (!window.confirm(t("scConfirmWithdraw"))) return;
		setBusyModelId(model.model_id);
		try {
			await fetchAPI(
				`/performance-models/${encodeURIComponent(model.model_id)}`,
				{
					method: "PUT",
					body: JSON.stringify({ is_published: false }),
				},
			);
			toast.success(t("scModelWithdrawn"));
			await load();
		} catch (error) {
			toast.error(`${t("error")}: ${error?.message || ""}`);
		} finally {
			setBusyModelId(null);
		}
	};

	const deleteModel = async (model) => {
		if (!window.confirm(t("scConfirmDelete"))) return;
		setBusyModelId(model.model_id);
		try {
			await fetchAPI(
				`/performance-models/${encodeURIComponent(model.model_id)}`,
				{
					method: "DELETE",
				},
			);
			toast.success(t("scModelDeleted"));
			await load();
		} catch (error) {
			toast.error(`${t("error")}: ${error?.message || ""}`);
		} finally {
			setBusyModelId(null);
		}
	};

	if (loading) {
		return (
			<div className="flex items-center justify-center h-64 text-muted-foreground">
				<Loader2 className="w-5 h-5 animate-spin mr-2" />
				{t("loading")}
			</div>
		);
	}

	return (
		<div className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-8 space-y-4">
			<div className="flex items-start justify-between gap-3 flex-wrap">
				<div className="min-w-0">
					<h1 className="font-heading text-2xl">{t("scTitle")}</h1>
					<p className="text-sm text-muted-foreground mt-0.5">
						{t("scSubtitle")}
					</p>
				</div>
				{!guideOpen && (
					<Button
						variant="ghost"
						size="sm"
						className="rounded-sm"
						onClick={() => setGuideOpen(true)}
					>
						<Info className="w-4 h-4 mr-1.5" />
						{t("scGuideShow")}
					</Button>
				)}
			</div>

			{guideOpen && (
				<Card className="rounded-sm border-volt/40 bg-volt/[0.04]">
					<CardContent className="p-4 space-y-3">
						<div className="flex items-start justify-between gap-3">
							<h2 className="font-heading text-base flex items-center gap-2">
								<Info className="w-4 h-4 text-volt" />
								{t("scGuideTitle")}
							</h2>
							<button
								type="button"
								onClick={dismissGuide}
								className="text-muted-foreground hover:text-foreground shrink-0"
								aria-label={t("close")}
							>
								<X className="w-4 h-4" />
							</button>
						</div>
						<dl className="space-y-2.5">
							{GUIDE_ROWS.map(([titleKey, bodyKey]) => (
								<div key={titleKey}>
									<dt className="text-xs font-medium uppercase tracking-wider text-volt">
										{t(titleKey)}
									</dt>
									<dd className="text-sm text-muted-foreground mt-0.5">
										{t(bodyKey)}
									</dd>
								</div>
							))}
						</dl>
					</CardContent>
				</Card>
			)}

			<Card className="bg-card border-border rounded-sm">
				<CardHeader className="pb-2">
					<CardTitle className="font-heading text-lg">
						{t("scFollowedTitle")}
					</CardTitle>
					<p className="text-sm text-muted-foreground">{t("scFollowedHint")}</p>
				</CardHeader>
				<CardContent className="space-y-3">
					<div className="flex flex-wrap gap-2">
						{choosable.map((sport) => {
							const selected = selectedSports.includes(sport.value);
							const isLocked = locked.has(sport.value) && !config?.is_admin;
							return (
								<button
									key={sport.value}
									type="button"
									aria-pressed={selected}
									disabled={isLocked}
									onClick={() => toggleSport(sport.value)}
									title={isLocked ? t("scShootingLocked") : undefined}
									className={`inline-flex items-center gap-1.5 rounded-sm border px-3 py-2 text-sm transition-colors ${
										selected
											? "border-volt/60 bg-volt/15 text-volt"
											: "border-border text-muted-foreground hover:border-volt/40"
									} ${isLocked ? "cursor-default" : ""}`}
								>
									{isLocked && <Lock className="w-3.5 h-3.5" />}
									{sport.label}
									{sportBadge(sport) && (
										<span className="text-[10px] uppercase tracking-wider opacity-70">
											{sportBadge(sport)}
										</span>
									)}
								</button>
							);
						})}
					</div>
					{hiddenShooting && !config?.is_admin && (
						<p className="text-xs text-muted-foreground">
							{t("scShootingLocked")}
						</p>
					)}
					<div className="flex flex-wrap items-center justify-between gap-2">
						{/* L'admin crea gli sport per tutti dal suo pannello; il coach
						    li aggiunge per sé, il suo team e i suoi atleti (D1). */}
						{config?.is_admin ? (
							<Button
								variant="ghost"
								size="sm"
								className="rounded-sm"
								onClick={() => navigate("/admin?tab=sports")}
							>
								<Plus className="w-4 h-4 mr-1.5" />
								{t("scManageSportsAdmin")}
							</Button>
						) : (
							<Button
								variant="ghost"
								size="sm"
								className="rounded-sm"
								onClick={() => setAddOpen(true)}
							>
								<Plus className="w-4 h-4 mr-1.5" />
								{t("scAddSport")}
							</Button>
						)}
						<Button
							className="bg-volt hover:bg-volt/90 rounded-sm"
							disabled={!sportsChanged || savingSports}
							onClick={saveSports}
						>
							{savingSports && (
								<Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
							)}
							{t("scSaveSports")}
						</Button>
					</div>
				</CardContent>
			</Card>

			<CreatedSportsCard sports={config?.created || []} onChanged={load} />

			{(config?.items || []).length === 0 ? (
				<Card className="rounded-sm">
					<CardContent className="py-10 text-center space-y-2">
						<Target className="w-8 h-8 mx-auto text-muted-foreground/60" />
						<p className="text-sm text-muted-foreground">{t("scNoSports")}</p>
					</CardContent>
				</Card>
			) : (
				(config.items || []).map((item) => {
					const own = item.own;
					const busy = (id) => busyModelId === id;
					// Uno sport creato da un coach parte senza modello di sistema:
					// finché l'admin non gliene dà uno, mostrarlo vuoto e proporre
					// di copiarlo confonde e basta.
					const systemIsEmpty =
						item.is_personal &&
						!(item.system?.counts?.domains || item.system?.counts?.exercises);
					return (
						<Card
							key={item.sport}
							className="bg-card border-border rounded-sm"
							data-testid={`sc-sport-${item.sport}`}
						>
							<CardHeader className="pb-2">
								<div className="flex items-start justify-between gap-3 flex-wrap">
									<CardTitle className="font-heading text-lg">
										{item.label ||
											sportsByValue[item.sport]?.label ||
											item.sport}
									</CardTitle>
									<div className="flex flex-wrap items-center gap-2">
										<Badge className="bg-secondary text-muted-foreground border-0 gap-1">
											<Users className="w-3 h-3" />
											{t("scYourAthletes").replace(
												"{count}",
												item.athletes ?? 0,
											)}
										</Badge>
										{item.is_personal && (
											<Badge className="bg-blaze/20 text-blaze border-0">
												{item.is_mine
													? t("scSportCreatedByYou")
													: t("scSportOfTeam")}
											</Badge>
										)}
										{!item.followed && (
											<Badge className="bg-amber-500/20 text-amber-300 border-0">
												{t("scNotFollowed")}
											</Badge>
										)}
									</div>
								</div>
							</CardHeader>
							<CardContent className="space-y-3">
								{/* Il modello di sistema */}
								{systemIsEmpty ? (
									<p className="text-xs text-muted-foreground">
										{t("scPersonalSportHint")}
									</p>
								) : (
									<ModelRow
										model={item.system}
										t={t}
										actions={
											<Button
												variant="outline"
												size="sm"
												className="rounded-sm"
												onClick={() => openModel(item.system.model_id)}
											>
												{item.system.can_edit ? (
													<Pencil className="w-3.5 h-3.5 mr-1.5" />
												) : (
													<Eye className="w-3.5 h-3.5 mr-1.5" />
												)}
												{item.system.can_edit ? t("scEdit") : t("scOpen")}
											</Button>
										}
									/>
								)}

								{/* Il modello del coach */}
								{own ? (
									<ModelRow
										model={own}
										t={t}
										highlight
										actions={
											<>
												<Button
													variant="outline"
													size="sm"
													className="rounded-sm"
													onClick={() => openModel(own.model_id)}
												>
													<Pencil className="w-3.5 h-3.5 mr-1.5" />
													{t("scEdit")}
												</Button>
												{own.is_published ? (
													<Button
														variant="outline"
														size="sm"
														className="rounded-sm"
														disabled={busy(own.model_id)}
														onClick={() => withdrawModel(own)}
													>
														<EyeOff className="w-3.5 h-3.5 mr-1.5" />
														{t("scWithdraw")}
													</Button>
												) : (
													<Button
														size="sm"
														className="rounded-sm bg-volt hover:bg-volt/90"
														disabled={busy(own.model_id)}
														onClick={() => setPublishTarget(own)}
													>
														<Eye className="w-3.5 h-3.5 mr-1.5" />
														{t("scPublish")}
													</Button>
												)}
												<Button
													variant="ghost"
													size="icon"
													className="h-8 w-8 rounded-sm hover:text-red-500"
													title={t("scDelete")}
													disabled={busy(own.model_id)}
													onClick={() => deleteModel(own)}
												>
													<Trash2 className="w-3.5 h-3.5" />
												</Button>
											</>
										}
									/>
								) : item.followed ? (
									<div className="rounded-sm border border-dashed border-border p-3 space-y-2">
										<p className="text-sm text-muted-foreground">
											{t("scNoOwnModel")}
										</p>
										<div className="flex flex-wrap gap-2">
											{!systemIsEmpty && (
												<Button
													size="sm"
													className="rounded-sm bg-volt hover:bg-volt/90"
													disabled={busy(`create:${item.sport}`)}
													onClick={() =>
														createModel(item.sport, item.system.model_id)
													}
												>
													<Copy className="w-3.5 h-3.5 mr-1.5" />
													{t("scCreateFromSystem")}
												</Button>
											)}
											<Button
												variant={systemIsEmpty ? "default" : "outline"}
												size="sm"
												className={`rounded-sm ${
													systemIsEmpty ? "bg-volt hover:bg-volt/90" : ""
												}`}
												disabled={busy(`create:${item.sport}`)}
												onClick={() => createModel(item.sport, null)}
											>
												<Plus className="w-3.5 h-3.5 mr-1.5" />
												{systemIsEmpty
													? t("scCreateOwnModel")
													: t("scCreateEmpty")}
											</Button>
											<Button
												variant="outline"
												size="sm"
												className="rounded-sm border-volt/40 text-volt hover:bg-volt/10"
												onClick={() => setAssistantSport(item)}
												data-testid={`sport-ai-btn-${item.sport}`}
											>
												<Bot className="w-3.5 h-3.5 mr-1.5" />
												{t("scCreateWithAssistant")}
											</Button>
										</div>
										{item.team.length > 0 && (
											<p className="text-xs text-amber-300/90">
												{t("scTeamConflictHint")}
											</p>
										)}
									</div>
								) : null}

								{/* I modelli dei colleghi di team */}
								{item.team.length > 0 && (
									<div className="space-y-2">
										<p className="text-[11px] uppercase tracking-wider text-muted-foreground">
											{t("scTeamModels")}
										</p>
										{item.team.map((model) => (
											<ModelRow
												key={model.model_id}
												model={model}
												t={t}
												actions={
													<Button
														variant="outline"
														size="sm"
														className="rounded-sm"
														onClick={() => openModel(model.model_id)}
													>
														<Eye className="w-3.5 h-3.5 mr-1.5" />
														{t("scOpen")}
													</Button>
												}
											/>
										))}
									</div>
								)}
							</CardContent>
						</Card>
					);
				})
			)}

			<AgentChatSheet
				open={!!assistantSport}
				onOpenChange={(open) => {
					if (!open) setAssistantSport(null);
				}}
				title={t("scSportAssistantTitle").replace("{sport}", assistantSport?.label || "")}
				description={t("scSportAssistantDescription")}
				modelSport={assistantSport?.sport || null}
				onModelsChanged={load}
			/>

			<AddSportDialog
				open={addOpen}
				onOpenChange={setAddOpen}
				onCreated={onSportCreated}
				onFollow={followSport}
			/>

			<PublishModelDialog
				model={publishTarget}
				open={!!publishTarget}
				onOpenChange={(open) => {
					if (!open) setPublishTarget(null);
				}}
				onPublished={() => {
					setPublishTarget(null);
					load();
				}}
			/>
		</div>
	);
}

function ModelRow({ model, t, actions, highlight = false }) {
	return (
		<div
			className={`flex flex-col gap-2 rounded-sm border p-3 sm:flex-row sm:items-center sm:justify-between ${
				highlight
					? "border-volt/40 bg-volt/[0.04]"
					: "border-border bg-secondary/30"
			}`}
		>
			<div className="min-w-0">
				<div className="flex flex-wrap items-center gap-2">
					<p className="font-medium truncate">{modelTitle(model, t)}</p>
					{!model.is_system && (
						<Badge
							className={`border-0 ${
								model.is_published
									? "bg-green-500/20 text-green-400"
									: "bg-secondary text-muted-foreground"
							}`}
						>
							{model.is_published ? t("scPublished") : t("scNotPublished")}
						</Badge>
					)}
				</div>
				<p className="text-xs text-muted-foreground mt-0.5">
					{modelOwnerText(model, t)} · {modelCountsText(model, t)} ·{" "}
					{t("scUsedBy").replace("{count}", model.athletes_using ?? 0)}
				</p>
			</div>
			<div className="flex flex-wrap items-center gap-2 shrink-0">
				{actions}
			</div>
		</div>
	);
}
